import { useRoute } from '@react-navigation/native';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  FlatList,
  useWindowDimensions,
  View,
} from 'react-native';
import { useToast } from 'react-native-toast-notifications';

import { Alert } from '@components/Alert';
import { DeleteFilesModal } from '@components/Modals/DeleteFilesModal.web';
import { NetInfoModal } from '@components/Modals/NetInfoModal.web';
import { ConvertedToTaskToast } from '@components/ProjectDetail/ProjectChecklist/ConvertedToTask';
import ProjectFileGridCard, {
  FileGridCardState,
} from '@components/Projects/ProjectFileGridCard.web';
import { Box, Text } from '@components/Restyle';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import { MediaViewer } from '@components/shared/MediaViewer.web';
import { Skeleton } from '@components/shared/Skeleton';
import { UpgradePaywallTooltip } from '@components/shared/UpgradePaywallTooltip';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import { PopupProjectFilesFilterButtonWeb } from '@components/Web/PopupProjectFilesFilterButton.web';
import {
  Document,
  DocumentAttributes,
  DocumentSortOption,
  GetProjectDocument,
  LocalFile,
  Project,
  TeamMemberRole,
  useAddProjectFilesMutation,
  useCreateShareLinkMutation,
  User,
} from '@graphql/generated';
import { useCopyLinkWithFailureToast } from '@hooks/useCopyLinkWithFailureToast.web';
import { useDeleteProjectDocument } from '@hooks/useDeleteProjectDocument';
import { useDeleteTaskFiles } from '@hooks/useDeleteTaskFile';
import { useDocumentSelection } from '@hooks/useDocumentSelection';
import useDownloadFiles from '@hooks/useDownloadFiles';
import useFileProcessor from '@hooks/useFileProcessor';
import { useGetDocumentsByClientIdsQuery } from '@hooks/useGetDocumentsByClientIdsQuery';
import { useListProjectDocumentsFromQuery } from '@hooks/useListProjectDocumentsFromQuery';
import useMe from '@hooks/useMe';
import { usePreviewDocuments } from '@hooks/usePreviewDocuments';
import { useProjectFromQuery } from '@hooks/useProjectFromQuery';
import { useProjectMembership } from '@hooks/useProjectMembership';
import { readShiftKey, useRangeHoverState } from '@hooks/useRangeHoverState';
import { useShareImagesLink } from '@hooks/useShareImagesLink.web';
import { useUploadFiles } from '@hooks/useUploadFiles';
import { FilesStackScreenProps } from '@navigation/files/files-stack';
import EmptyStateProjectFiles from '@screens/Projects/EmptyStateProjectFiles.web';
import {
  computeSelectionUpdate,
  MAX_SELECTABLE_FILES,
} from '@screens/Projects/projectFilesSelection';
import { useNetInfo } from '@src/context/NetInfoContext';
import theme, { getScaledSize, SCALED_DIMENSIONS } from '@themes/theme';
import {
  copyTextToClipboard,
  CopyTextResult,
} from '@utils/copyTextToClipboard';
import { useFilePicker } from '@utils/filePicker';
import { fileToLocalFile } from '@utils/fileUtils';
import {
  filterShareableDocuments,
  getDocumentIds,
  isMediaSelection,
} from '@utils/getDocumentIds';
import { compressImages } from '@utils/imageCompression';
import { isSubscriptionAccessible } from '@utils/teams/isSubscriptionAccessible';
import { shouldShowPaywallForProject } from '@utils/teams/paywall';

import type { UploadProgressData } from 'expo-file-system/legacy';

export enum FilterType {
  NoFilter = '',
  MyUploads = 'MyUploads',
}

export interface ProjectFilesProps {
  projectId: Project['id'];
  projectUUId: Project['uuid'];
  sortBy?: DocumentSortOption;
  ownerId?: User['id'];
  isSelectMode: boolean;
  onSelectedModeChange: (isSelected: boolean) => void;
  selectedList: Array<Document | LocalFile>;
  handelSelectedItem: (item: Document | LocalFile) => void;
  deleteloader?: boolean;
  isViewer?: boolean;
}

export const handleDeleteDocs = (
  selectedDocuments: any[],
  deleteProjectFileById: (
    docs: any[],
    chatId: string,
    cb?: () => void,
    onError?: () => void
  ) => void,
  deleteTaskFileById: (
    docs: any[],
    chatId: string,
    cb?: () => void,
    onError?: () => void
  ) => void,
  resetSelection: () => void,
  onDeleteError?: () => void
) => {
  const projectDocs =
    selectedDocuments?.flatMap((item) => item.projectDocuments) || [];
  const taskDocs =
    selectedDocuments?.flatMap((item) => item.taskDocuments) || [];
  const messagedoc =
    selectedDocuments.map((item: any) => item?.messageDocuments) || [];
  const chatId = messagedoc[0]?.map((item: any) => item.message?.chatId) || '';

  const handleError = onDeleteError ?? resetSelection;

  if (taskDocs.length > 0) {
    deleteTaskFileById(
      taskDocs,
      chatId[0],
      () => {
        if (projectDocs.length > 0) {
          deleteProjectFileById(
            projectDocs,
            chatId[0],
            resetSelection,
            handleError
          );
        } else {
          resetSelection();
        }
      },
      handleError
    );
  } else if (projectDocs.length > 0) {
    deleteProjectFileById(projectDocs, chatId[0], resetSelection, handleError);
  } else {
    resetSelection();
  }
};

type GridFileItem = {
  item: Document | LocalFile;
  state: FileGridCardState;
  progress?: number;
};

export interface ProjectFilesRef {
  isSelectMode: boolean;
  selectedDocuments: Array<Document | LocalFile>;
  setIsSelectMode: (value: boolean) => void;
  setSelectedDocuments: (documents: Array<Document | LocalFile>) => void;
  isTeamOwnerOrAdmin: boolean;
  project?: Project | null;
  downloadFiles: (
    files: Array<Document | LocalFile>,
    arg1: boolean,
    arg2: boolean
  ) => void;
  isDownloading: boolean;
  shareLoading: boolean;
  showLinkCopied: boolean;
  handleSharePress: () => Promise<void> | void;
  setShowDeleteModal: (value: boolean) => void;
}

interface ProjectFilesComponentProps {
  onSelectionStateChange?: (state: ProjectFilesRef) => void;
}

const ProjectFiles = forwardRef<ProjectFilesRef, ProjectFilesComponentProps>(
  ({ onSelectionStateChange }, ref) => {
    const { me } = useMe();
    const { t } = useTranslation('models');
    const { isConnected } = useNetInfo();
    const { width: windowWidth } = useWindowDimensions();
    const [isNetInfoModalVisible, setIsNetInfoModalVisible] = useState(false);
    const { downloadFiles, isDownloading, downloadError, clearDownloadError } =
      useDownloadFiles();
    const { setOnDropFile, setPaywallModalOpen, setPaywallTeam } =
      useWebDrawer();
    const [filterBy, setFilterBy] = useState<FilterType>(FilterType.NoFilter);
    const [isImageCarouselVisible, setImageCarouselVisible] = useState(false);
    const [filterMedia, setFilterMedia] = useState(false);
    const [filterDocument, setFilterDocument] = useState(false);
    const [searchUploader, setSearchUploader] = useState('');
    const [uploadHover, setUploadHover] = useState(false);

    const route = useRoute<FilesStackScreenProps<'project-files'>['route']>();
    const [projectId, setProjectId] = useState<string>();

    const { projectUUId } = route.params;
    const { project } = useProjectFromQuery({
      projectUUId: projectUUId || '',
    });

    useEffect(() => {
      setProjectId(project?.id);
    }, [project]);

    const { isViewer } = useProjectMembership(project);
    const {
      isSelectMode,
      setIsSelectMode,
      selectedDocuments,
      setSelectedDocuments,
      toggleSelectItem: _handelSelectedItem,
    } = useDocumentSelection();

    const { launchFileSelection } = useFilePicker();
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const { deleteProjectFileById } = useDeleteProjectDocument();
    const { deleteTaskFileById } = useDeleteTaskFiles();

    const { checkClientIds } = useGetDocumentsByClientIdsQuery();
    const { setPreviewDocuments, setActiveIndex } = usePreviewDocuments();

    const {
      uploadingFiles,
      uploadingErrorFiles,
      setUploadingFiles,
      setUploadingErrorFiles,
      setUploadingSuccessFiles,
      setProjectId: setUploadProjectId,
      setProjectUUId: setUploadProjectUUId,
    } = useUploadFiles();

    // Track the total count and file IDs of the current upload batch
    const [currentBatchTotal, setCurrentBatchTotal] = useState(0);
    const [currentBatchFileIds, setCurrentBatchFileIds] = useState<Set<string>>(
      new Set()
    );

    useEffect(() => {
      if (projectId) {
        setUploadProjectId(projectId);
      }
      if (projectUUId) {
        setUploadProjectUUId(projectUUId);
      }
    }, [projectId, projectUUId, setUploadProjectId, setUploadProjectUUId]);

    const toast = useToast();
    // Same red-icon failure toast as MediaViewer's copy-link (TA-10845).
    const { copyLink, showFailure, failureToast } =
      useCopyLinkWithFailureToast();

    // Get all project members
    const projectMembers = project?.members || [];

    // Filter members based on search uploader text
    const getFilteredOwnerIds = useMemo(() => {
      // If MyUploads filter is active, use current user ID
      if (filterBy === FilterType.MyUploads && me?.id) {
        return [me.id];
      }

      // If search text is provided, filter members by name
      if (searchUploader.trim()) {
        const searchLower = searchUploader.toLowerCase().trim();
        const matchingMembers = projectMembers.filter((member) => {
          const user = member?.user;
          if (!user) return false;

          const name = user.name || '';

          return name.toLowerCase().includes(searchLower);
        });

        // Extract user IDs from matching members
        const matchingIds = matchingMembers
          .map((member) => member?.user?.id)
          .filter((id): id is string => !!id);

        // If search text exists but no members match, return ['0'] to filter out all documents
        if (matchingIds.length === 0) {
          return ['0'];
        }

        return matchingIds;
      }

      // No filter applied
      return undefined;
    }, [projectMembers, searchUploader, filterBy, me?.id]);

    // Determine includeMedia and includeFiles based on filter selection
    // If Media only selected: includeMedia: true
    // If Document only selected: includeFiles: true
    // If both or none selected: don't pass anything (show all)
    const getIncludeParams = () => {
      const onlyMedia = filterMedia && !filterDocument;
      const onlyDocument = filterDocument && !filterMedia;

      if (onlyMedia) {
        return { includeMedia: true };
      } else if (onlyDocument) {
        return { includeFiles: true };
      }
      // If both selected or none selected, don't pass anything (show all)
      return {};
    };

    // Query both files and media together
    const [loadingMore, setLoadingMore] = useState(false);
    const { documents, isPreviousResult, loading, pageInfo, fetchFromCursor } =
      useListProjectDocumentsFromQuery({
        projectIds: projectId ? [projectId] : undefined,
        // projectId is set from an effect, so the first render has none. An
        // unscoped query would read any cached `projectIds: undefined` result
        // (other projects' files) and keep showing it as the fallback while
        // this project's documents load.
        skip: !projectId,
        ownerIds: getFilteredOwnerIds,
        sortBy: DocumentSortOption.DateDesc,
        first: 30,
        onFetchMoreCompleted: () => setLoadingMore(false),
        ...getIncludeParams(),
      });

    const loadMoreDocuments = useCallback(() => {
      // `loading` doesn't flip during fetchMore, so guard on `loadingMore`
      // — otherwise repeated onEndReached fires duplicate fetchMores with
      // the same cursor. No `loading` guard: during the background refresh
      // the cached list is on screen, and the hook defers the request until
      // the refresh lands instead of dropping it.
      if (!pageInfo?.hasNextPage || loadingMore) return;
      setLoadingMore(true);
      fetchFromCursor();
    }, [pageInfo?.hasNextPage, loadingMore, fetchFromCursor]);

    // Deduplicate by ID — Apollo's fetchMore updateQuery can introduce
    // duplicate edges when pagination overlaps with a concurrent refetch.
    const dedupedDocuments = useMemo(() => {
      const seenDocIds = new Set<string>();
      return (documents ?? []).filter((doc) => {
        if (seenDocIds.has(doc.id)) return false;
        seenDocIds.add(doc.id);
        return true;
      }) as Document[];
    }, [documents]);

    // True while the open viewer shows the live query list (vs a single-item
    // snapshot), i.e. its navigation set should follow pagination (TA-9771).
    const viewerFollowsListRef = useRef(false);

    useEffect(() => {
      if (selectedDocuments && selectedDocuments.length === 0) {
        setAnchorIndex(null); // Clear anchor index when selection is cleared
      }
    }, [selectedDocuments]);

    // ============================
    // Shift/Ctrl multiselect logic
    // ============================
    const [anchorIndex, setAnchorIndex] = useState<number | null>(null);
    // Hovered index + Shift state for the range preview. Both come from the
    // pointer events below as well as the keyboard, because Shift tracked only
    // from window keydown/keyup left the preview stuck off whenever the page
    // missed the keydown or a window blur cleared it — while Shift-click kept
    // working, since it reads shiftKey off the click event itself (TA-10163).
    const { isInHoverRange, onItemHover, onItemHoverOut } =
      useRangeHoverState(anchorIndex);

    // Stable key for selection across Document/LocalFile
    const getItemKey = (x: any) => x.clientId ?? x.file?.clientId ?? x.id;

    const selectedKeySet = useMemo(() => {
      return new Set(
        (selectedDocuments ?? []).map((it: any) => getItemKey(it))
      );
    }, [selectedDocuments]);

    const { processFiles } = useFileProcessor();

    const [addProjectFiles] = useAddProjectFilesMutation({
      refetchQueries: [
        'listActivitiesByProject',
        'listProjectDocuments',
        ...(projectUUId
          ? [{ query: GetProjectDocument, variables: { id: projectUUId } }]
          : []),
      ],
    });

    const callback = (
      documentClientId: string,
      uploadProgress: UploadProgressData
    ) => {
      const progress =
        uploadProgress.totalBytesSent / uploadProgress.totalBytesExpectedToSend;

      setUploadingFiles((prev) =>
        prev.map((item) =>
          item.file.clientId === documentClientId ? { ...item, progress } : item
        )
      );
    };

    const getPhotos = useCallback(
      (imageList: LocalFile[]) => {
        const documents = imageList.map((item, index): Document => {
          const {
            name,
            clientId,
            contentType,
            isImage = false,
            isAudio = false,
            duration = null,
          } = item;
          const documentId = `${projectId}-${index}`;

          return {
            __typename: 'Document',
            id: documentId,
            owner: me!,
            name: name || clientId,
            clientId,
            contentType: contentType,
            isImage,
            createdAt: new Date().toISOString(),
            size: item.size,
            file: {
              ...item,
              __typename: 'File',
              id: documentId,
              cdnBaseUrl: '',
              url: item.url,
            },
            isAudio,
            duration,
            expired: false,
            failedAt: '',
            isVideo: false,
            uuid: documentId,
          };
        });

        const newUploadingItems = documents.map((doc) => ({
          file: doc,
          progress: 0,
        }));
        const newFileIds = new Set(documents.map((doc) => doc.id));

        setUploadingFiles([...uploadingFiles, ...newUploadingItems]);

        // Track file IDs for this batch
        setCurrentBatchFileIds(newFileIds);

        // Clear previous successful uploads when starting a new batch (they're already in the documents list)
        setUploadingSuccessFiles([]);

        // Calculate total count for this batch: new files + any ongoing files from this batch
        // Don't include failed files from previous batches
        const ongoingFromThisBatch = uploadingFiles.filter((item) =>
          newFileIds.has(item.file.id)
        ).length;
        const batchTotal = ongoingFromThisBatch + documents.length;
        setCurrentBatchTotal(batchTotal);

        processFiles(documents, callback).then(
          (response) => {
            projectId &&
              addProjectFiles({
                variables: {
                  id: projectId,
                  attributes: imageList.map((item): DocumentAttributes => {
                    const {
                      name,
                      clientId,
                      contentType,
                      isImage = false,
                      isAudio = false,
                    } = item;
                    const { blobId } =
                      response.find(
                        (uploadResultItem) =>
                          uploadResultItem.clientId === clientId
                      ) || {};

                    return {
                      name,
                      contentType,
                      isImage,
                      isAudio,
                      blobId,
                      clientId,
                    } as DocumentAttributes;
                  }),
                },
                onCompleted: () => {
                  checkClientIds(response.map((newItem) => newItem.clientId));

                  setUploadingSuccessFiles([...documents]);
                  setUploadingFiles([]);
                },
                onError: (e) => {
                  if (
                    e.message === t('models:projects.files.storageLimitReached')
                  ) {
                    Alert.alert(
                      'TaskTag',
                      t('models:projects.files.storageLimitReached')
                    );
                    setUploadingFiles([]);
                    return;
                  }
                  setUploadingErrorFiles([...documents]);
                  setUploadingFiles([]);
                },
              });
          },
          () => {
            setUploadingErrorFiles([...documents]);
            setUploadingFiles([]);
          }
        );
      },
      [
        addProjectFiles,
        checkClientIds,
        processFiles,
        projectId,
        uploadingFiles,
        uploadingErrorFiles,
      ]
    );

    const getPhotosCheck = useCallback(
      (imageList: LocalFile[]) => {
        if (shouldShowPaywallForProject(project)) {
          setPaywallTeam(project?.team ?? undefined);
          setPaywallModalOpen(true);
          return;
        }
        if (!isConnected) {
          setIsNetInfoModalVisible(true);
          return;
        }
        setIsNetInfoModalVisible(false);
        getPhotos(imageList);
      },
      [isConnected, getPhotos, project, setPaywallTeam, setPaywallModalOpen]
    );

    // Use a ref to store the latest getPhotosCheck to avoid recreating onDrop
    const getPhotosCheckRef = useRef(getPhotosCheck);
    useEffect(() => {
      getPhotosCheckRef.current = getPhotosCheck;
    }, [getPhotosCheck]);

    const isTeamMember = project?.currentUserTeamRole
      ? project.currentUserTeamRole === TeamMemberRole.Member
      : false;

    const isTeamOwnerOrAdmin = project?.currentUserTeamRole
      ? project.currentUserTeamRole === TeamMemberRole.Owner ||
        project.currentUserTeamRole === TeamMemberRole.Admin
      : false;
    const disableUpload = !isSubscriptionAccessible(
      project?.team?.subscriptionDetails
        ? {
            ...project.team.subscriptionDetails,
            subscriptionStatus:
              project.team.subscriptionDetails.subscriptionStatus || undefined,
          }
        : undefined,
      project?.team?.storageRemaining,
      project?.team?.personal,
      project?.currentUserRole || undefined
    );
    const shouldEnableSelectMode = (
      isSelected: boolean,
      isViewer: boolean,
      isTeamMember: boolean
    ) => isSelected && (!isViewer || isTeamMember || isTeamOwnerOrAdmin);

    const [createShareLink, { loading: shareLoading }] =
      useCreateShareLinkMutation();
    const { onShareImages } = useShareImagesLink();

    const [showLinkCopied, setShowLinkCopied] = useState(false);

    const customToast = (message: string) => {
      toast.show(<ConvertedToTaskToast message={message} duration={3000} />, {
        type: 'custom',
        duration: 3000,
        placement: 'bottom',
        animationType: 'slide-in',
        style: {
          transform: [{ scale: 0.7 }], // scale down existing toast so it follows the design
          backgroundColor: 'transparent',
          shadowColor: 'transparent',
        },
      });
    };

    const copyToClipboard = useCallback(
      async (link: string | null | undefined) => {
        if (await copyLink(link)) setShowLinkCopied(true);
      },
      [copyLink]
    );

    useEffect(() => {
      if (showLinkCopied) {
        const timer = setTimeout(() => {
          setShowLinkCopied(false);
        }, 3000);
        return () => clearTimeout(timer);
      }
    }, [showLinkCopied]);

    useEffect(() => {
      if (downloadError) {
        customToast(t(downloadError));
        clearDownloadError();
      }
    }, [downloadError, clearDownloadError, t]);

    const handleSharePress = useCallback(async () => {
      if (shouldShowPaywallForProject(project)) {
        setPaywallTeam(project?.team ?? undefined);
        setPaywallModalOpen(true);
        return;
      }
      const isMedia = isMediaSelection(selectedDocuments);
      if (isMedia) {
        const url = await onShareImages(selectedDocuments);
        await copyToClipboard(url);
      } else if (typeof navigator.share === 'function') {
        // Same LocalFile-truncation risk as the isMedia branch (onShareImages)
        // above — exclude anything that isn't a persisted Document before
        // sharing. — TA-10212
        const shareableDocuments = filterShareableDocuments(selectedDocuments);
        if (shareableDocuments.length === 0) return;
        const documentId = getDocumentIds(shareableDocuments);
        try {
          // Generate share link
          await createShareLink({
            variables: {
              documentIds: documentId,
            },
            onCompleted: ({ createShareLink }) => {
              copyToClipboard(createShareLink?.sharedUrl);
            },
          });
        } catch (error) {
          console.error('Error generating share link:', error);
          showFailure('link-generation-failed');
        }
      }
    }, [
      project,
      setPaywallTeam,
      setPaywallModalOpen,
      selectedDocuments,
      onShareImages,
      copyToClipboard,
      createShareLink,
      showFailure,
    ]);

    const handleViewerCopyLink = useCallback(
      async (docs: Document[]): Promise<CopyTextResult | undefined> => {
        if (shouldShowPaywallForProject(project)) {
          setPaywallTeam(project?.team ?? undefined);
          setPaywallModalOpen(true);
          // Paywall modal is its own feedback — signal "handled" so the
          // viewer doesn't also flash a copy-succeeded/failed toast.
          return undefined;
        }
        // Prefer /images-preview/ URL for media; fall back to createShareLink
        // if onShareImages returns null (e.g. the mutation failed).
        const isMedia = isMediaSelection(docs);
        if (isMedia) {
          const url = await onShareImages(docs);
          if (url) {
            return copyTextToClipboard(url);
          }
        }
        const documentIds = getDocumentIds(docs);
        try {
          const result = await createShareLink({
            variables: { documentIds },
          });
          const sharedUrl = result.data?.createShareLink?.sharedUrl;
          if (sharedUrl) {
            return copyTextToClipboard(sharedUrl);
          }
          return { success: false, reason: 'link-generation-failed' };
        } catch (error) {
          console.error('Error generating share link:', error);
          return { success: false, reason: 'link-generation-failed' };
        }
      },
      [
        project,
        setPaywallTeam,
        setPaywallModalOpen,
        onShareImages,
        createShareLink,
      ]
    );

    // Expose selection state and functions via ref
    const refValue: ProjectFilesRef = {
      isSelectMode,
      selectedDocuments,
      setIsSelectMode,
      setSelectedDocuments,
      isTeamOwnerOrAdmin,
      project,
      downloadFiles,
      isDownloading,
      shareLoading,
      showLinkCopied,
      handleSharePress,
      setShowDeleteModal,
    };

    useImperativeHandle(ref, () => refValue, [
      isSelectMode,
      selectedDocuments,
      isTeamOwnerOrAdmin,
      project,
      downloadFiles,
      isDownloading,
      shareLoading,
      showLinkCopied,
      handleSharePress,
    ]);

    // Notify parent when selection state changes.
    useEffect(() => {
      if (onSelectionStateChange) {
        onSelectionStateChange(refValue);
      }
    }, [
      isSelectMode,
      selectedDocuments.length,
      shareLoading,
      showLinkCopied,
      onSelectionStateChange,
      isTeamOwnerOrAdmin,
      project?.id,
      isDownloading,
      handleSharePress,
      downloadFiles,
    ]);

    const onDrop = useCallback(async (files: File[]) => {
      if (!files?.length) return;

      try {
        const localFiles = await Promise.all(
          files.map((file) => fileToLocalFile(file))
        );

        const compressedLocalFiles = await compressImages(localFiles);
        getPhotosCheckRef.current(compressedLocalFiles);
      } catch (error) {
        console.error('Error processing dropped files:', error);
      }
    }, []);

    // Store onDrop in a ref to ensure stable reference
    const onDropRef = useRef(onDrop);
    useEffect(() => {
      onDropRef.current = onDrop;
    }, [onDrop]);

    // Register the drop handler with MainPanel
    useEffect(() => {
      const dropHandler = async (files: File[]) => {
        await onDropRef.current(files);
      };
      setOnDropFile(() => dropHandler);
      return () => setOnDropFile(undefined);
    }, []);

    // Merge uploaded documents with uploading and failed files
    const gridItems: GridFileItem[] = useMemo(() => {
      const items: GridFileItem[] = [];
      const seenIds = new Set<string>();

      // Add uploaded documents
      documents.forEach((doc) => {
        if (!seenIds.has(doc.id)) {
          seenIds.add(doc.id);
          items.push({
            item: doc,
            state: 'uploaded',
          });
        }
      });

      // Add uploading files
      uploadingFiles.forEach((uploadingItem) => {
        if (!seenIds.has(uploadingItem.file.id)) {
          seenIds.add(uploadingItem.file.id);
          items.push({
            item: uploadingItem.file,
            state: 'uploading',
            progress: uploadingItem.progress,
          });
        }
      });

      // Add failed files
      uploadingErrorFiles.forEach((failedItem) => {
        if (failedItem && !seenIds.has(failedItem.id)) {
          seenIds.add(failedItem.id);
          items.push({
            item: failedItem,
            state: 'failed',
          });
        }
      });

      // Sort by createdAt (most recent first)
      return items.sort((a, b) => {
        const aIsDocument = a.item.__typename === 'Document';
        const bIsDocument = b.item.__typename === 'Document';
        const aDate = aIsDocument
          ? (a.item as Document).createdAt
          : new Date().toISOString();
        const bDate = bIsDocument
          ? (b.item as Document).createdAt
          : new Date().toISOString();
        return new Date(bDate).getTime() - new Date(aDate).getTime();
      });
    }, [documents, uploadingFiles, uploadingErrorFiles]);

    // Get grid items for range selection (matches what's actually rendered)
    const gridItemsForSelection = useMemo(() => {
      return gridItems.map((gi) => gi.item);
    }, [gridItems]);

    const gridItemsKeyMap = useMemo(() => {
      const m = new Map<string, Document | LocalFile>();
      gridItemsForSelection.forEach((it: any) => m.set(getItemKey(it), it));
      return m;
    }, [gridItemsForSelection]);

    const gridItemKeys = useMemo(
      () => gridItemsForSelection.map((it: any) => getItemKey(it)),
      [gridItemsForSelection]
    );

    // Compute next selected list based on shift/ctrl keys
    const computeNextSelectedList = useCallback(
      (
        clicked: Document | LocalFile,
        index: number,
        e?: any,
        inSelectMode?: boolean
      ) => {
        const ne = e?.nativeEvent;
        const {
          keys,
          anchorIndex: nextAnchorIndex,
          limitExceeded,
        } = computeSelectionUpdate({
          itemKeys: gridItemKeys,
          selectedKeys: selectedKeySet,
          clickedKey: getItemKey(clicked),
          clickedIndex: index,
          anchorIndex,
          shiftKey: !!ne?.shiftKey,
          ctrlOrCmdKey: !!ne?.ctrlKey || !!ne?.metaKey,
          inSelectMode: !!inSelectMode,
        });

        if (limitExceeded) {
          Alert.alert(
            `Maximum ${MAX_SELECTABLE_FILES} files can be selected at a time.`
          );
        }
        if (!keys) return selectedDocuments;

        if (nextAnchorIndex !== anchorIndex) setAnchorIndex(nextAnchorIndex);

        const next: Array<Document | LocalFile> = [];
        gridItemsForSelection.forEach((it: any) => {
          const key = getItemKey(it);
          if (keys.has(key)) next.push(gridItemsKeyMap.get(key) ?? it);
        });

        return next;
      },
      [
        gridItemKeys,
        gridItemsForSelection,
        gridItemsKeyMap,
        anchorIndex,
        selectedKeySet,
        selectedDocuments,
      ]
    );

    // Set anchor index from first selected item if not set
    useEffect(() => {
      if (anchorIndex !== null) return;
      if (!selectedDocuments?.length) return;

      const firstKey = getItemKey(selectedDocuments[0] as any);
      const idx = gridItemsForSelection.findIndex(
        (x: any) => getItemKey(x) === firstKey
      );
      if (idx >= 0) setAnchorIndex(idx);
    }, [anchorIndex, selectedDocuments, gridItemsForSelection]);

    // Calculate file counts
    // activeCount: number of files currently being uploaded (ongoing) from current batch
    const activeCount = uploadingFiles.filter((item) =>
      currentBatchFileIds.has(item.file.id)
    ).length;

    // totalCount: total files in current upload batch (ongoing + failed + successfully uploaded in this batch)
    // Use currentBatchTotal if available (when there are active uploads), otherwise calculate from current state
    const failedFromThisBatch = uploadingErrorFiles.filter(
      (item) => item && currentBatchFileIds.has(item.id)
    ).length;

    const totalCount =
      activeCount > 0 && currentBatchTotal > 0
        ? currentBatchTotal
        : activeCount + failedFromThisBatch;

    // Reset batch total and file IDs when all uploads from current batch are complete
    useEffect(() => {
      const hasActiveFromBatch = uploadingFiles.some((item) =>
        currentBatchFileIds.has(item.file.id)
      );
      const hasFailedFromBatch = uploadingErrorFiles.some(
        (item) => item && currentBatchFileIds.has(item.id)
      );

      if (
        !hasActiveFromBatch &&
        !hasFailedFromBatch &&
        currentBatchFileIds.size > 0
      ) {
        setCurrentBatchTotal(0);
        setCurrentBatchFileIds(new Set());
      }
    }, [uploadingFiles, uploadingErrorFiles, currentBatchFileIds]);

    // Determine number of columns based on screen width
    // Desktop > 1280: 6 columns
    // Desktop < 1280: 4 columns
    // Tablet (768-1280): 4 columns
    // Mobile (< 768): 2 columns
    let numColumns;
    if (windowWidth > 1280) {
      numColumns = 6;
    } else if (windowWidth >= 768) {
      numColumns = 4;
    } else {
      numColumns = 2;
    }
    const [bodyWidth, setBodyWidth] = useState(0);
    const cardSpacing = theme.spacing.m; // Match the marginRight used in renderItem
    const containerPadding = theme.spacing.m * 2; // paddingHorizontal on both sides
    const cardWidth =
      bodyWidth > 0
        ? (bodyWidth - containerPadding - (numColumns - 1) * cardSpacing) /
          numColumns
        : 0;
    const skeletonCardWidth = cardWidth > 0 ? cardWidth : 160;

    const handleFilePress = (
      item: Document | LocalFile,
      index: number,
      e?: any,
      forceSelection?: boolean
    ) => {
      const ne = e?.nativeEvent;
      const shiftKey = !!ne?.shiftKey;
      const ctrlOrCmdKey = !!ne?.ctrlKey || !!ne?.metaKey;

      // If selecting, modifier keys pressed, or forced selection (from radio button) -> selection
      if (isSelectMode || shiftKey || ctrlOrCmdKey || forceSelection) {
        const inSelectMode =
          isSelectMode ||
          (selectedDocuments?.length ?? 0) > 0 ||
          forceSelection;
        const next = computeNextSelectedList(item, index, e, inSelectMode);
        setSelectedDocuments(next);
        if (!isSelectMode && next.length > 0) {
          setIsSelectMode(shouldEnableSelectMode(true, isViewer, isTeamMember));
        }
        return;
      }

      // Otherwise open/preview
      const isDocument = item.__typename === 'Document';
      if (isDocument) {
        const foundIdx = dedupedDocuments.findIndex(
          (d) => d.id === (item as Document).id
        );
        // Only follow the live query list when the tapped doc belongs to it —
        // otherwise (e.g. a just-uploaded file not in the list yet) keep the
        // single-item snapshot and skip pagination sync.
        viewerFollowsListRef.current = foundIdx >= 0;
        setPreviewDocuments(
          foundIdx >= 0 ? dedupedDocuments : [item as Document]
        );
        setActiveIndex(Math.max(foundIdx, 0));
        setImageCarouselVisible(true);
      }
    };

    // Keep the open viewer in sync with the paginated query results so newly
    // fetched pages extend the viewer's navigation set (TA-9771). Synced by id
    // signature — `documents` is rebuilt every render, so syncing on identity
    // would loop (setPreviewDocuments → context render → new array → effect).
    const dedupedIdsSignature = useMemo(
      () => dedupedDocuments.map((d) => d.id).join(','),
      [dedupedDocuments]
    );
    useEffect(() => {
      if (!isImageCarouselVisible || !viewerFollowsListRef.current) return;
      setPreviewDocuments(dedupedDocuments);
    }, [dedupedIdsSignature, isImageCarouselVisible]);

    const handleFileLongPress = (item: Document | LocalFile, index: number) => {
      setIsSelectMode(shouldEnableSelectMode(true, isViewer, isTeamMember));
      const item0 = selectedDocuments?.find((item2) => {
        return item2.id == item.id;
      });
      if (!item0) {
        setSelectedDocuments([...selectedDocuments, item]);
        setAnchorIndex(index);
      }
    };

    const handleRetry = async (item: Document | LocalFile) => {
      if (item.__typename !== 'Document' || !projectId) return;

      const document = item as Document;

      // Remove from error files
      setUploadingErrorFiles((prev) =>
        prev.filter((doc) => doc?.id !== document.id)
      );

      // Add to uploading files with progress 0
      setUploadingFiles((prev) => [...prev, { file: document, progress: 0 }]);

      // If file is not in current batch, add it and update batch total
      if (!currentBatchFileIds.has(document.id)) {
        setCurrentBatchFileIds((prev) => new Set([...prev, document.id]));
        setCurrentBatchTotal((prev) => prev + 1);
      }

      // Retry upload using processFiles and addProjectFiles
      try {
        const response = await processFiles([document], callback);

        await addProjectFiles({
          variables: {
            id: projectId,
            attributes: [
              {
                name: document.name,
                contentType: document.contentType || '',
                isImage: document.isImage || false,
                isAudio: document.isAudio || false,
                blobId:
                  response.find((r) => r.clientId === document.clientId)
                    ?.blobId || '',
                clientId: document.clientId,
              } as DocumentAttributes,
            ],
          },
          onCompleted: () => {
            checkClientIds(response.map((newItem) => newItem.clientId));
            setUploadingSuccessFiles([document]);
            setUploadingFiles((prev) =>
              prev.filter((item) => item.file.id !== document.id)
            );
          },
          onError: () => {
            setUploadingErrorFiles((prev) => [...prev, document]);
            setUploadingFiles((prev) =>
              prev.filter((item) => item.file.id !== document.id)
            );
          },
        });
      } catch (error) {
        console.error('Retry upload failed:', error);
        setUploadingErrorFiles((prev) => [...prev, document]);
        setUploadingFiles((prev) =>
          prev.filter((item) => item.file.id !== document.id)
        );
      }
    };

    const handleDeleteFailed = (item: Document | LocalFile) => {
      if (item.__typename === 'Document') {
        // Simply remove from error files state
        setUploadingErrorFiles((prev) =>
          prev.filter((doc) => doc?.id !== item.id)
        );
      }
    };

    const handleUploadPress = async () => {
      await launchFileSelection().then((r) => {
        if (!r) return;

        const documents = r.map((item) => {
          return {
            ...item,
            path: '',
            isAudio: false,
            isImage: false,
          };
        });
        getPhotosCheck(documents);
      });
    };

    const handleFilterMediaChange = (selected: boolean) => {
      setFilterMedia(selected);
    };

    const handleFilterDocumentChange = (selected: boolean) => {
      setFilterDocument(selected);
    };

    const handleSearchChange = (searchText: string) => {
      setSearchUploader(searchText);
    };

    const handleClearAllFilters = () => {
      setFilterMedia(false);
      setFilterDocument(false);
      setSearchUploader('');
      setFilterBy(FilterType.NoFilter);
    };

    // Calculate active filter count
    const activeFilterCount = useMemo(() => {
      let count = 0;
      if (filterBy !== FilterType.NoFilter) count++;
      if (filterMedia) count++;
      if (filterDocument) count++;
      if (searchUploader.trim()) count++;
      return count;
    }, [filterBy, filterMedia, filterDocument, searchUploader]);

    const renderItem = ({
      item: gridItem,
      index: flatListIndex,
    }: {
      item: GridFileItem;
      index: number;
    }) => {
      // Use the flatListIndex directly since gridItemsForSelection matches gridItems order
      const actualIndex = flatListIndex;
      const itemKey = getItemKey(gridItem.item);

      const isSelected = selectedKeySet.has(itemKey);
      const isInRange = isInHoverRange(actualIndex);

      return (
        <View
          style={{
            width: cardWidth,
            marginRight: theme.spacing.m,
            marginBottom: theme.spacing.m,
          }}
          // @ts-ignore - onMouseEnter/onMouseMove/onMouseLeave are web-only props
          onMouseEnter={(ev: any) => onItemHover(actualIndex, readShiftKey(ev))}
          // Re-reading Shift on every move lets the preview recover from a missed
          // keydown or a stale window blur (TA-10163).
          onMouseMove={(ev: any) => onItemHover(actualIndex, readShiftKey(ev))}
          onMouseLeave={() => onItemHoverOut(actualIndex)}>
          <ProjectFileGridCard
            item={gridItem.item}
            state={gridItem.state}
            progress={gridItem.progress}
            isSelectMode={isSelectMode}
            isSelected={!!isSelected}
            isInRange={isInRange}
            cardWidth={cardWidth}
            onPress={(item, e, forceSelection) =>
              handleFilePress(item, actualIndex, e, forceSelection)
            }
            onLongPress={(item) => handleFileLongPress(item, actualIndex)}
            onRetry={handleRetry}
            onDelete={handleDeleteFailed}
          />
        </View>
      );
    };

    return (
      <>
        <Box mx='xs' mt='xxs' flex={1}>
          {/* Grid View */}
          {/* isPreviousResult: after a filter/uploader change to uncached
              variables the hook falls back to the previous list, so keep
              the skeleton instead of showing it as the new result. */}
          {(loading || !projectId) &&
          (documents.length === 0 || isPreviousResult) ? (
            <Box flex={1} position='relative'>
              <Skeleton
                variant='rect'
                width={140}
                height={32}
                borderRadius='s'
                style={{ marginBottom: theme.spacing.m }}
              />
              <Box
                flex={1}
                onLayout={(e) => setBodyWidth(e.nativeEvent.layout.width)}>
                <Box
                  flexDirection='row'
                  flexWrap='wrap'
                  gap='m'
                  paddingHorizontal='m'>
                  {[0, 1, 2, 3, 4, 5].map((index) => (
                    <Skeleton
                      key={index}
                      variant='rect'
                      width={skeletonCardWidth}
                      height={skeletonCardWidth}
                      borderRadius='s'
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          ) : (
            <Box flex={1} position='relative'>
              <Box
                flexDirection='row'
                alignItems='center'
                justifyContent='space-between'
                marginBottom='m'>
                <Box flexDirection='row' alignItems='center' gap='xxs'>
                  {/* Filter Button */}
                  <PopupProjectFilesFilterButtonWeb
                    onSelectMedia={handleFilterMediaChange}
                    onSelectDocument={handleFilterDocumentChange}
                    onSearchChange={handleSearchChange}
                    onClearAll={handleClearAllFilters}
                    mediaSelected={filterMedia}
                    documentSelected={filterDocument}
                    searchValue={searchUploader}
                    filterCount={activeFilterCount}
                    accessibilityLabel='Files & Media Filter Button'
                    boxProps={{
                      position: 'bottom',
                      offset: theme.spacing.s,
                    }}
                  />

                  <UpgradePaywallTooltip
                    show={!!project && shouldShowPaywallForProject(project)}>
                    <Box
                      onMouseEnter={() => setUploadHover(true)}
                      onMouseLeave={() => setUploadHover(false)}>
                      <Button
                        isSmall
                        disabled={disableUpload}
                        height={getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT)}
                        prefix={
                          <Icon variant='s' name='UploadCloud' color='grey05' />
                        }
                        onPress={handleUploadPress}
                        borderRadius='xs'
                        variant='upload'
                        backgroundColor={
                          uploadHover && !disableUpload ? 'grey01' : 'white'
                        }
                        borderWidth={0}
                        prefixMarginRight='xs'
                        paddingHorizontal='m'
                        accessibilityLabel={t('projects.files.upload')}>
                        {t('projects.files.upload')}
                      </Button>
                    </Box>
                  </UpgradePaywallTooltip>

                  {activeCount > 0 && (
                    // add spinner
                    <Box flexDirection='row' alignItems='center' gap='s'>
                      <ActivityIndicator
                        size='small'
                        color={theme.colors.textPrimary}
                      />
                      <Text variant='labelSmall' color='textSecondary'>
                        {activeCount} of {totalCount} files
                      </Text>
                    </Box>
                  )}
                </Box>
              </Box>
              <Box
                flex={1}
                onLayout={(e) => setBodyWidth(e.nativeEvent.layout.width)}>
                {bodyWidth > 0 && (
                  <FlatList
                    key={numColumns}
                    data={gridItems}
                    renderItem={renderItem}
                    keyExtractor={(item) => `${item.item.id}-${item.state}`}
                    numColumns={numColumns}
                    contentContainerStyle={{
                      paddingHorizontal: theme.spacing.m,
                      paddingBottom: theme.spacing.listFooter,
                      flexGrow: gridItems.length === 0 ? 1 : undefined,
                    }}
                    ListEmptyComponent={() => (
                      <EmptyStateProjectFiles
                        onDropZoneClick={handleUploadPress}
                      />
                    )}
                    ListFooterComponent={() => {
                      if (pageInfo?.hasNextPage) {
                        return (
                          <Box marginTop='m'>
                            <ActivityIndicatorLoading />
                          </Box>
                        );
                      }
                      return null;
                    }}
                    onEndReached={loadMoreDocuments}
                    onEndReachedThreshold={0.5}
                    showsVerticalScrollIndicator={false}
                  />
                )}
              </Box>
            </Box>
          )}
        </Box>

        <DeleteFilesModal
          showModal={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={() => {
            handleDeleteDocs(
              selectedDocuments,
              deleteProjectFileById,
              deleteTaskFileById,
              () => {
                setSelectedDocuments([]);
                setIsSelectMode(false);
                customToast(
                  selectedDocuments.length > 1
                    ? t('files.filesDeleted')
                    : t('files.fileDeleted')
                );
              },
              () => {
                setSelectedDocuments([]);
                setIsSelectMode(false);
                customToast(t('files.deleteFileFailed'));
              }
            );
          }}
          fileCount={selectedDocuments.length}
        />

        {isNetInfoModalVisible && (
          <NetInfoModal
            onClose={() => setIsNetInfoModalVisible(false)}
            showModal={isNetInfoModalVisible}
            title={t('models:chat.netInfoTitle')}
            message={t('models:chat.netInfoMessage')}
            buttonTextOne={t('models:onBoarding.error.okPress')}
          />
        )}

        {isImageCarouselVisible && (
          <MediaViewer
            onDismiss={() => setImageCarouselVisible(false)}
            isViewerOnly={isViewer}
            onCopyLink={handleViewerCopyLink}
            hasNextPage={
              viewerFollowsListRef.current && !!pageInfo?.hasNextPage
            }
            loadingMore={loadingMore}
            onLoadMore={loadMoreDocuments}
          />
        )}
        {failureToast}
      </>
    );
  }
);

ProjectFiles.displayName = 'ProjectFiles';

export default ProjectFiles;
